import React, { useCallback, useMemo } from "react"

import { ANNOTATION_VALUE } from "@/constants/annotation-values"
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
} from "@coval/ui"
import { NumberInput, Select, SelectItem } from "@heroui/react"
import { CornerDownRight, Info, ListFilter, X } from "lucide-react"

import { TRACKING_EVENTS, useAnalytics } from "@/lib/analytics"
import {
  FLOAT_COMPARISON_OPERATOR,
  SET_COMPARISON_OPERATOR,
  type ComparisonOperator,
  type FloatComparisonOperator,
  type MetricRecordData,
  type SetComparisonOperator,
  type TargetCondition,
} from "@/types/metrics/contracts"

import { INFO_ICON_STYLES, LABEL_ROW_STYLES, LABEL_STYLES } from "../styles"

interface TargetFilterToggleProps {
  metricRecordData: MetricRecordData
  onChange: (metric: MetricRecordData) => void
}

const comparisonOperators = {
  manager_numerical_llm_judge: {
    eq: "= equals",
    neq: "≠ doesn't equal",
    gt: "> greater than",
    gte: "≥ greater than or equal",
    lt: "< less than",
    lte: "≤ less than or equal",
  },
  manager_llm_binary: {
    in: "= equals",
  },
  manager_default_categorical: {
    in: "∈ contains",
    nin: "∉ doesn't contain",
  },
}

export const TargetFilterToggle: React.FC<TargetFilterToggleProps> = ({
  metricRecordData,
  onChange,
}) => {
  const { trackEvent } = useAnalytics()

  const metricManager =
    (metricRecordData?.metric_manager as keyof typeof comparisonOperators) || ""

  const currentOperators =
    comparisonOperators[metricManager] ??
    (metricManager.endsWith("categorical")
      ? comparisonOperators.manager_default_categorical
      : metricManager.endsWith("llm_binary")
        ? comparisonOperators.manager_llm_binary
        : comparisonOperators.manager_numerical_llm_judge)

  // Default operator for the current metric manager
  const defaultOperator = useMemo(() => {
    if (
      !metricManager.endsWith("categorical") &&
      !metricManager.endsWith("llm_binary")
    ) {
      return FLOAT_COMPARISON_OPERATOR.EQ
    }
    return SET_COMPARISON_OPERATOR.IN
  }, [metricManager])

  const assignDefaultTargetCondition = useCallback(() => {
    // Track with posthog
    trackEvent(TRACKING_EVENTS.METRICS.TARGET_CONDITION_CREATED, {
      organization_id: metricRecordData.organization_id,
      organization_slug: metricRecordData.organization_slug,
      metric_name: metricRecordData.metric_name,
      metric_display_name: metricRecordData.metric_display_name,
    })

    let target_condition: TargetCondition

    if (metricManager.endsWith("categorical")) {
      target_condition = {
        comparison_operator: defaultOperator as SetComparisonOperator,
        target_values: [],
      }
    } else if (metricManager.endsWith("llm_binary")) {
      target_condition = {
        comparison_operator: defaultOperator as SetComparisonOperator,
        target_values: [ANNOTATION_VALUE.YES],
      }
    } else {
      target_condition = {
        comparison_operator: defaultOperator as FloatComparisonOperator,
        target_float: 0,
      }
    }

    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        target_condition,
      },
    })
  }, [metricRecordData, onChange, defaultOperator, metricManager, trackEvent])

  const removeTargetCondition = useCallback(() => {
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        target_condition: undefined,
      },
    })
  }, [metricRecordData, onChange])

  // Initialize state from existing metadata - use useMemo to make it reactive
  const existingTargetCondition = useMemo(() => {
    return metricRecordData?.metric_metadata?.target_condition
  }, [metricRecordData?.metric_metadata?.target_condition])

  // Compute values directly from existingTargetCondition (falling back to defaults)
  const comparisonOperator = useMemo(() => {
    return existingTargetCondition?.comparison_operator || defaultOperator
  }, [existingTargetCondition?.comparison_operator, defaultOperator])

  const targetFloat = useMemo(() => {
    return existingTargetCondition && "target_float" in existingTargetCondition
      ? existingTargetCondition.target_float
      : undefined
  }, [existingTargetCondition])

  const targetBinaryValue = useMemo(() => {
    return existingTargetCondition &&
      "target_values" in existingTargetCondition &&
      existingTargetCondition.target_values?.[0]
      ? existingTargetCondition.target_values[0]
      : ANNOTATION_VALUE.YES
  }, [existingTargetCondition])

  // Get categories from metric metadata for categorical metrics
  const categories = useMemo(() => {
    return (
      metricRecordData?.metric_metadata?.additional_information?.value
        ?.split(",")
        .filter(Boolean) || []
    )
  }, [metricRecordData?.metric_metadata?.additional_information?.value])

  const targetCategoricalValues = useMemo(() => {
    return new Set<string>(
      existingTargetCondition && "target_values" in existingTargetCondition
        ? existingTargetCondition.target_values
        : [],
    )
  }, [existingTargetCondition])

  // Helper function to update metadata based on metric type
  const updateTargetMetadata = (
    comparisonOp: ComparisonOperator,
    floatValue?: number,
    categoricalValues?: Set<string>,
  ) => {
    const newMetadata = { ...metricRecordData.metric_metadata }

    if (metricType === "numerical") {
      if (floatValue !== undefined) {
        onChange({
          ...metricRecordData,
          metric_metadata: {
            ...newMetadata,
            target_condition: {
              comparison_operator: comparisonOp as FloatComparisonOperator,
              target_float: floatValue,
            },
          },
        })
      }
    } else {
      const values = categoricalValues ? Array.from(categoricalValues) : []

      onChange({
        ...metricRecordData,
        metric_metadata: {
          ...newMetadata,
          target_condition: {
            comparison_operator: comparisonOp as SetComparisonOperator,
            target_values: values,
          },
        },
      })
    }
  }

  // Helper function to determine metric type
  const metricType = useMemo(() => {
    if (metricManager.endsWith("categorical")) {
      return "categorical"
    } else if (metricManager.endsWith("llm_binary")) {
      return "binary"
    }
    return "numerical"
  }, [metricManager])

  return (
    <div className="flex flex-col gap-2">
      <div className={LABEL_ROW_STYLES}>
        <label className={LABEL_STYLES}>Success Criteria</label>
        <Tooltip
          content="Success criteria determines the condition that the metric must meet to be considered successful"
          side="top"
        >
          <button type="button" className="inline-flex" aria-label="Info">
            <Info className={INFO_ICON_STYLES} />
          </button>
        </Tooltip>
      </div>
      {existingTargetCondition ? (
        <>
          <div className="flex flex-row items-center justify-between">
            <span className="text-sm text-coval-text-muted">
              <span className="italic">
                {metricRecordData.metric_display_name ||
                  metricRecordData.metric_name ||
                  "New Metric"}
              </span>{" "}
              succeeds when
            </span>
            <Button
              icon
              variant="ghost"
              onClick={removeTargetCondition}
              className="text-coval-text-muted hover:text-coval-danger-text"
              tooltip="Remove success criteria"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex flex-row w-full items-center pl-1">
            <CornerDownRight className="w-4 h-4 text-coval-text-muted mr-1" />
            <div className="flex flex-row gap-2 w-full">
              <Button
                disabled
                variant="secondary"
                className="px-4 rounded-md shrink-0 text-coval-text-muted"
              >
                <span className="font-medium">
                  {metricRecordData.metric_display_name ||
                    metricRecordData.metric_name ||
                    "New Metric"}
                </span>
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="secondary"
                    className="rounded-md shrink-0"
                    disabled={metricType === "binary"}
                  >
                    <span className="font-medium">
                      {(currentOperators as Record<string, string>)[
                        comparisonOperator
                      ] || ""}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  {Object.entries(currentOperators).map(([key, label]) => (
                    <DropdownMenuItem
                      key={key}
                      onSelect={() => {
                        const newOperator = key as ComparisonOperator
                        if (metricType === "numerical") {
                          updateTargetMetadata(newOperator, targetFloat)
                        } else if (metricType === "binary") {
                          updateTargetMetadata(
                            newOperator,
                            undefined,
                            new Set([targetBinaryValue]),
                          )
                        } else {
                          updateTargetMetadata(
                            newOperator,
                            undefined,
                            targetCategoricalValues,
                          )
                        }
                      }}
                    >
                      <span className="font-medium">{label}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {(() => {
                switch (metricType) {
                  case "binary":
                    return (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="secondary"
                            className="rounded-md shrink-0"
                          >
                            <span className="font-medium">
                              {targetBinaryValue}
                            </span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                          <DropdownMenuItem
                            onSelect={() =>
                              updateTargetMetadata(
                                comparisonOperator,
                                undefined,
                                new Set([ANNOTATION_VALUE.YES]),
                              )
                            }
                          >
                            YES
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onSelect={() =>
                              updateTargetMetadata(
                                comparisonOperator,
                                undefined,
                                new Set([ANNOTATION_VALUE.NO]),
                              )
                            }
                          >
                            NO
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )
                  case "categorical":
                    return (
                      <Select
                        selectionMode="multiple"
                        selectedKeys={targetCategoricalValues}
                        onSelectionChange={(keys) => {
                          const newValues = keys as Set<string>
                          updateTargetMetadata(
                            comparisonOperator,
                            undefined,
                            newValues,
                          )
                        }}
                        placeholder="Select categories..."
                        classNames={{
                          base: "flex-1 min-w-0",
                          trigger: "h-auto! rounded-md",
                        }}
                      >
                        {categories.map((category) => (
                          <SelectItem key={category}>{category}</SelectItem>
                        ))}
                      </Select>
                    )
                  case "numerical":
                  default:
                    return (
                      <NumberInput
                        hideStepper
                        placeholder="Enter value..."
                        value={targetFloat}
                        onValueChange={(value: number) => {
                          updateTargetMetadata(comparisonOperator, value)
                        }}
                        classNames={{
                          base: "flex-1 min-w-0",
                          inputWrapper: "h-auto! rounded-md",
                        }}
                      />
                    )
                }
              })()}
            </div>
          </div>
        </>
      ) : (
        <Button
          variant="secondary"
          className="w-56"
          onClick={assignDefaultTargetCondition}
        >
          <ListFilter className="w-4 h-4" />
          Add Success Criteria
        </Button>
      )}
    </div>
  )
}
